import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { AppearancePicker } from "components/common/AppearancePicker";
import {
  AssetIcon,
  MODEL_COLORS,
  NUMBERED_ASSET_COUNT,
} from "components/common/AssetIcon";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { SpacePickerMenu } from "@/components/main/spaces/SpacePickerMenu";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  NamingOverlay,
  type NamingOverlayHandle,
} from "@/components/main/utils/NamingOverlay";
import {
  attemptShown,
  nameLength,
  overLimitMessage,
  retryMessage,
  type NamingMessage,
  type NamingPhase,
} from "@/components/main/utils/naming";
import { MAX_MODEL_NAME_CHARS } from "@/components/main/utils/models/modelName";
import {
  glideRows,
  snapshotRows,
  type RowSnapshot,
} from "@/components/main/overview/glideRows";
import type {
  CreateModelOutcome,
  CreateModelTarget,
  NewModelDraft,
} from "@/components/main/utils/models/useCreateModel";

/// Where the card says the model goes. A space target with no id is the
/// Overview with nothing focused: there is no default space, so the chip asks.
export type NewModelTarget = { spaceId: string | null } | { catalog: true };

interface NewModelOverlayProps {
  open: boolean;
  target: NewModelTarget;
  /// The list the new row lands in — the card sits on its centre and, on
  /// success, shrinks into the row.
  anchorRef: React.RefObject<HTMLElement | null>;
  paneRef: React.RefObject<HTMLElement | null>;
  createModel: (
    target: CreateModelTarget,
    draft: NewModelDraft,
  ) => Promise<CreateModelOutcome>;
  onClose: () => void;
}

const pickRandom = <T,>(items: readonly T[]): T =>
  items[Math.floor(Math.random() * items.length)];

/// Restarts the settle-in outline: it began when the row mounted under the card.
const replaySettle = (row: HTMLElement) => {
  row.style.animation = "none";
  void row.offsetWidth;
  row.style.animation = "";
};

export const NewModelOverlay: React.FC<NewModelOverlayProps> = ({
  open,
  target: initialTarget,
  anchorRef,
  paneRef,
  createModel,
  onClose,
}) => {
  const overlayRef = useRef<NamingOverlayHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const glyphRef = useRef<HTMLButtonElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);

  const [name, setName] = useState("");
  const [color, setColor] = useState(() => pickRandom(MODEL_COLORS));
  const [icon, setIcon] = useState(() =>
    Math.floor(Math.random() * NUMBERED_ASSET_COUNT),
  );
  const [target, setTarget] = useState<NewModelTarget>(initialTarget);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [phase, setPhase] = useState<NamingPhase>("idle");
  const [refusal, setRefusal] = useState<string | null | undefined>();
  // Set when ↵ found no space: the pick that follows finishes the create.
  const pendingCreate = useRef(false);

  const spaceById = useOrgContextStore((s) => s.spaceById);
  const models = useMainStateStore((s) => s.availableModels);

  // The rows as they stood before the create: in the commit the new row lands
  // in, the others glide down from there, and the new row waits for the card.
  const rowsBefore = useRef<RowSnapshot | null>(null);
  useLayoutEffect(() => {
    const before = rowsBefore.current;
    if (!before || !paneRef.current) return;
    rowsBefore.current = null;
    glideRows(before, paneRef.current, { holdNew: true });
  }, [models, paneRef]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setColor(pickRandom(MODEL_COLORS));
    setIcon(Math.floor(Math.random() * NUMBERED_ASSET_COUNT));
    setTarget(initialTarget);
    setAppearanceOpen(false);
    setPickerOpen(false);
    setPhase("idle");
    setRefusal(undefined);
    pendingCreate.current = false;
    // Reset per opening only; a changed target prop mid-edit must not wipe it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const length = nameLength(name);
  const over = length > MAX_MODEL_NAME_CHARS;
  const space =
    "spaceId" in target && target.spaceId ? spaceById(target.spaceId) : null;

  const duplicate =
    trimmed.length > 0 &&
    models.some(
      (m) =>
        !m.scheduled_deletion_date &&
        ("catalog" in target
          ? m.is_catalog
          : !!space && m.space_id === space.space_id) &&
        (m.name ?? "").trim().toLowerCase() === trimmed.toLowerCase(),
    );

  const message: NamingMessage | null =
    refusal !== undefined
      ? refusal === null
        ? retryMessage("model")
        : { kind: "error", content: refusal }
      : over
        ? overLimitMessage(length, MAX_MODEL_NAME_CHARS)
        : duplicate
          ? {
              kind: "warn",
              content: (
                <>
                  {space ? <b>{space.name}</b> : "The catalog"} already has a
                  model named <b>{trimmed}</b>
                </>
              ),
            }
          : null;

  const cancel = async () => {
    if (phase !== "idle") return;
    setPhase("leaving");
    await overlayRef.current?.leave();
    onClose();
  };

  const submit = async (spaceOverride?: string) => {
    if (phase !== "idle" || !trimmed || over) return;
    const createTarget: CreateModelTarget | null =
      "catalog" in target
        ? { catalog: true }
        : (spaceOverride ?? target.spaceId)
          ? { spaceId: (spaceOverride ?? target.spaceId) as string }
          : null;
    if (!createTarget) {
      pendingCreate.current = true;
      setAppearanceOpen(false);
      setPickerOpen(true);
      return;
    }
    setAppearanceOpen(false);
    setPickerOpen(false);
    setRefusal(undefined);
    setPhase("creating");
    const startedAt = performance.now();
    if (anchorRef.current) rowsBefore.current = snapshotRows(anchorRef.current);
    const outcome = await createModel(createTarget, {
      name: trimmed,
      color,
      icon,
    });
    if (!outcome.ok) {
      rowsBefore.current = null;
      await attemptShown(startedAt);
      setPhase("idle");
      setRefusal(outcome.message);
      inputRef.current?.focus();
      return;
    }
    setPhase("leaving");
    const modelId = String(outcome.model.model_id);
    await overlayRef.current?.landIn(
      () =>
        Array.from(
          anchorRef.current?.querySelectorAll<HTMLElement>("[data-model-id]") ??
            [],
        ).find((el) => el.dataset.modelId === modelId) ?? null,
      replaySettle,
    );
    onClose();
  };

  const closePicker = () => {
    pendingCreate.current = false;
    setPickerOpen(false);
    inputRef.current?.focus();
  };

  const busy = phase !== "idle";

  return (
    <>
      <NamingOverlay
        ref={overlayRef}
        open={open}
        ariaLabel={"catalog" in target ? "New catalog model" : "New model"}
        anchorRef={anchorRef}
        paneRef={paneRef}
        inputRef={inputRef}
        placeholder="Model name"
        name={name}
        onNameChange={(next) => {
          setName(next);
          setRefusal(undefined);
        }}
        maxChars={MAX_MODEL_NAME_CHARS}
        message={message}
        phase={phase}
        canCreate={!!trimmed}
        glyph={<AssetIcon size="sm" color={color} iconIndex={icon} />}
        glyphLabel="Icon and color"
        glyphRef={glyphRef}
        glyphExpanded={appearanceOpen}
        onGlyphClick={() => {
          setPickerOpen(false);
          setAppearanceOpen((isOpen) => !isOpen);
        }}
        footStart={
          "catalog" in target ? (
            <span className="naming-target">
              <PublicOutlinedIcon sx={{ fontSize: 12 }} />
              <span className="naming-target-name">Catalog</span>
            </span>
          ) : (
            <button
              ref={chipRef}
              type="button"
              className={`naming-target ${space ? "" : "is-empty"}`}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
              aria-label={space ? `Space: ${space.name}. Change` : undefined}
              disabled={busy}
              onClick={() => {
                if (pickerOpen) return closePicker();
                setAppearanceOpen(false);
                setPickerOpen(true);
              }}
            >
              {space && <SpaceDot color={spaceColor(space)} />}
              <span className="naming-target-name">
                {space ? space.name : "Choose a space"}
              </span>
              <KeyboardArrowDownIcon
                className="naming-target-chevron"
                sx={{ fontSize: 14 }}
              />
            </button>
          )
        }
        onSubmit={() => void submit()}
        onCancel={() => void cancel()}
      />
      <AppearancePicker
        isOpen={open && appearanceOpen}
        onClose={() => {
          setAppearanceOpen(false);
          inputRef.current?.focus();
        }}
        anchorRef={glyphRef}
        currentColor={color}
        onColorChange={setColor}
        currentIcon={icon}
        onIconChange={setIcon}
      />
      {"spaceId" in target && (
        <SpacePickerMenu
          anchorRef={chipRef}
          isOpen={open && pickerOpen}
          onClose={closePicker}
          selectedSpaceId={target.spaceId}
          placement="bottom"
          onPick={(spaceId) => {
            const finish = pendingCreate.current;
            pendingCreate.current = false;
            setTarget({ spaceId });
            setRefusal(undefined);
            if (finish) void submit(spaceId);
          }}
        />
      )}
    </>
  );
};
